import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import {
  Button,
  Card,
  ControlGroup,
  Icon,
  InputGroup,
  Label,
  NumericInput,
  Popover,
  Slider
} from '@blueprintjs/core'
import { uniqueId, defer, delay, debounce } from 'lodash'
import { InsertFormClass } from '../classes'
import CanvasDraw from 'react-canvas-draw'
import { hasScrollbarAlways } from '../../../../../helper/CSSclasses'
import { OverlayProps } from '../../../../../helper/configs'
import { ChromePicker } from 'react-color'
import { useTranslation } from 'react-i18next'

function EditorMini ({ contentData, setContentData }, ref) {
  const refForCanvas = useRef(null)

  useEffect(() => {
    if (contentData) {
      refForCanvas.current.loadSaveData(contentData.data, true)
    } else {
      refForCanvas.current.clear()
    }
  }, [contentData])

  useImperativeHandle(ref, () => ({
    getCurrentContentData: () => {
      return { data: refForCanvas.current.getSaveData(), size: { width: 250, height: 250 } }
    },
    clearCurrentEditor: refForCanvas.current.clear
  }), []
  )

  return (
    <div css={css`cursor: none; width: 250px; height: 250px; overflow: visible; `}><CanvasDraw
      brushRadius={1} canvasWidth={250} canvasHeight={250}
      ref={refForCanvas} catenaryColor={'transparent'} immediateLoading loadTimeOffset={0} lazyRadius={0} /></div>
  )
}
// eslint-disable-next-line no-func-assign
EditorMini = forwardRef(EditorMini)

function EditorBig ({ contentData, setContentData }, ref) {
  if (process.env.NODE_ENV !== 'production') {
    console.tron.display({
      name: 'EditorBig',
      value: {
        contentData
      }
    // , important: true
    })
  }
  const refForCanvas = useRef(null)

  const [brushRadius, setBrushRadius] = useState((contentData && contentData.brushRadius) || 1)

  const [color, setColor] = useState((contentData && contentData.color) || { r: 18, g: 15, b: 26, a: 1 })

  const [width, setWidth] = useState(contentData ? contentData.size.width : 400)
  const [height, setHeight] = useState(contentData ? contentData.size.height : 400)

  const setByProp = useCallback(
    () => {
      if (process.env.NODE_ENV !== 'production') {
        console.tron.log('EditorBig setByProp')
      }
      if (contentData) {
        if (process.env.NODE_ENV !== 'production') {
          console.tron.log('EditorBig setByProp 1')
        }
        setWidth(contentData.size.width)
        setHeight(contentData.size.height)
        delay(() => {
          refForCanvas.current.loadSaveData(contentData.data, true)
        }, 500)
      } else {
        if (process.env.NODE_ENV !== 'production') {
          console.tron.log('EditorBig setByProp 2')
        }
        refForCanvas.current.clear()
      }
    },
    [contentData]
  )

  useEffect(setByProp, [setByProp])

  useImperativeHandle(ref, () => ({
    getCurrentContentData: () => {
      return { data: refForCanvas.current.getSaveData(), size: { width: width, height: height }, color, brushRadius }
    },
    clearCurrentEditor: refForCanvas.current.clear,
    reset: setByProp
  }), [brushRadius, color, height, setByProp, width]
  )

  const { t } = useTranslation()

  return (
    <div css={css`display: flex; flex-direction: column; height: 100%; width: 100%; background-color: var(--baseColor); position: absolute;`}>
      <Card css={css`overflow: auto; position: relative; flex: auto; padding: 0; background-color: var(--gray3) !important;`} className={hasScrollbarAlways}>
        <div css={css`cursor: none; width: 0; height: 0; overflow: visible; `}>
          <CanvasDraw
            brushRadius={brushRadius} brushColor={`rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`} canvasWidth={width} canvasHeight={height}
            ref={refForCanvas} catenaryColor={'transparent'} immediateLoading lazyRadius={0} /></div>
      </Card>
      <div className={hasScrollbarAlways} css={css`
      max-height: 20%; overflow: auto; padding-top: 2px;
      display: flex; flex-direction: row; flex-wrap: wrap; justify-content: flex-start; align-items: center; align-content: space-around;
        & > * {
          margin-left: 10px;
        }
        & input {
          width: 70px !important;
        }
      `}>
        <Button text={t('Undo')} icon={'undo'} onClick={() => {
          refForCanvas.current.undo()
        }} />
        <span css={css`display: flex;`}><span css={css`margin: auto 0 auto 0;`}>Brush</span>
          <Popover {...OverlayProps} content={<ChromePicker color={color} onChangeComplete={(colorObj) => { setColor(colorObj.rgb) }} />}>
            <Button icon={<Icon icon={'tint'} color={`rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`} />} css={css`margin-left: 5px;`} />
          </Popover>
          <Slider initialValue={1} value={brushRadius} onChange={setBrushRadius} onRelease={setBrushRadius} max={100} css={css`margin-left: 10px;`} />
        </span>
        <ControlGroup>
          <NumericInput leftIcon='arrows-horizontal' value={width} onValueChange={(valueAsNumber) => { setWidth(valueAsNumber) }} clampValueOnBlur stepSize={10} majorStepSize={100} minorStepSize={1} max={1000} min={1} />
          <NumericInput leftIcon='arrows-vertical' value={height} onValueChange={(valueAsNumber) => { setHeight(valueAsNumber) }} clampValueOnBlur stepSize={10} majorStepSize={100} minorStepSize={1} max={1000} min={1} />
        </ControlGroup>
      </div>
    </div>
  )
}
// eslint-disable-next-line no-func-assign
EditorBig = forwardRef(EditorBig)

function Render ({ contentData }) {
  const refForCanvas = useRef(null)

  const [width, setWidth] = useState(contentData ? contentData.size.width : 400)
  const [height, setHeight] = useState(contentData ? contentData.size.height : 400)

  const setByProp = useCallback(
    () => {
      if (contentData) {
        setWidth(contentData.size.width)
        setHeight(contentData.size.height)
        // TODO 여기서 delay 처리한게 불안함... defer로는 안돼서 걍 delay 썼는데 다르게 안되나?
        delay(() => {
          refForCanvas.current.loadSaveData(contentData.data, true)
        }, 200)
      } else {
        refForCanvas.current.clear()
      }
    },
    [contentData]
  )

  const canvasDraw = useMemo(() => (<CanvasDraw
    brushRadius={0} disabled hideGrid canvasWidth={width} canvasHeight={height}
    ref={refForCanvas} catenaryColor={'transparent'} immediateLoading lazyRadius={0} />), [height, width])

  useEffect(setByProp, [setByProp])

  return (
    canvasDraw
  )
}

const standardHeight = 60
function ThumbNailBase ({ contentData }) {
  const refForCanvas = useRef(null)

  const [width, setWidth] = useState(contentData ? contentData.size.width : 400)
  const [height, setHeight] = useState(contentData ? contentData.size.height : 400)

  // const [containerWidth, setContainerWidth] = useState(null)
  // const [containerHeight, setContainerHeight] = useState(null)

  const setByProp = useCallback(
    () => {
      if (contentData) {
        setWidth(contentData.size.width)
        setHeight(contentData.size.height)
        delay(() => {
          refForCanvas.current.loadSaveData(contentData.data, true)
        }, 200)
      } else {
        refForCanvas.current.clear()
      }
    },
    [contentData]
  )

  const canvasDraw = useMemo(() => (<CanvasDraw
    brushRadius={0} disabled hideGrid canvasWidth={width} canvasHeight={height}
    ref={refForCanvas} catenaryColor={'transparent'} immediateLoading lazyRadius={0} />), [height, width])

  useEffect(setByProp, [setByProp])

  // 이 thumbnail의 container는 position: relative 같은 거여서 position: absolute의 기준이 될 수 있어야 함
  // const resizeSensor = useMemo(() => (<ResizeSensor onResize={(entries) => {
  //   const width = entries[0].contentRect.width
  //   // const height = e.contentRect.height
  //   setContainerWidth(width)
  //   // setContainerHeight(height)
  // }}>
  //   <div css={css`position: absolute; width: 80%; height: 0; appearance: none;`} />
  // </ResizeSensor>), [])

  const card = useMemo(() => (<Card css={css`padding: 0; margin: 1px !important;`} interactive>
    <div
      style={{
        transform: ((width > standardHeight ? `scaleX(${standardHeight / width})` : '') + ' ' +
          (height > standardHeight ? `scaleY(${standardHeight / height})` : '')),
        width: `${width > standardHeight ? standardHeight : width}px`,
        height: `${height > standardHeight ? standardHeight : height}px`,
        position: 'relative',
        right: (width > standardHeight ? (((width - standardHeight) / 2) * (standardHeight / width)) : 0) + 'px',
        bottom: (height > standardHeight ? (((height - standardHeight) / 2) * (standardHeight / height)) : 0) + 'px'
      }}>
      {canvasDraw}
    </div>
  </Card>), [canvasDraw, height, width])

  return (
    <React.Fragment>
      {/* {resizeSensor} */}
      {card}
    </React.Fragment>
  )
}

const ForDrawing = new InsertFormClass({
  typeName: 'Drawing',
  EditorMini,
  Render,
  ThumbNailBase,
  EditorBig })

export default ForDrawing
